import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { ArrowLeft, Loader2, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, resendOTP, user } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef([]);

  // Get email from navigation state or user
  const email = location.state?.email || user?.email;
  const userRole = location.state?.userRole || user?.role;

  useEffect(() => {
    // Focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // Countdown timer for resend
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedOtp = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedOtp.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + pastedOtp.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      toast.error('Veuillez entrer le code complet');
      return;
    }

    if (!email) {
      toast.error('Email non trouve. Veuillez recommencer.');
      navigate('/register');
      return;
    }

    setLoading(true);
    try {
      await verifyOTP(email, otpCode);
      setVerified(true);
      toast.success('E-mail vérifié avec succès !');
      // Ne pas rediriger automatiquement - afficher la page de confirmation
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Code OTP invalide';
      toast.error(errorMsg);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Email non trouve');
      return;
    }

    setResending(true);
    try {
      await resendOTP(email);
      setResendTimer(60);
      toast.success('Nouveau code envoye a votre email!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(errorMsg);
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl rounded-2xl max-w-md w-full overflow-hidden">
          {/* Header gradient bar */}
          <div className="h-2 bg-gradient-to-r from-[#E53935] via-[#2196F3] to-[#1A237E]" />
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 
              className="text-2xl font-bold text-[#1A237E] mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              E-mail vérifié !
            </h2>
            <p className="text-slate-600 mb-6">
              Votre compte a été activé avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-[#E53935] hover:bg-[#D32F2F] text-white font-semibold rounded-xl"
              data-testid="go-to-login-button"
            >
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-blue-50 flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <Logo />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-red-500" />
              </div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Verification OTP
              </CardTitle>
              <CardDescription className="text-base">
                Entrez le code a 6 chiffres envoye a
                <br />
                <span className="font-medium text-slate-700">{email || 'votre email'}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {/* OTP Input */}
              <div className="flex justify-center gap-2 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                    data-testid={`otp-input-${index}`}
                  />
                ))}
              </div>

              {/* Spam Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5" data-testid="spam-warning">
                <p className="text-xs text-amber-700 text-center">
                  <span className="font-medium">📧 Vous ne trouvez pas l'email ?</span>
                  <br />
                  Vérifiez votre dossier <strong>Spam</strong> ou <strong>Courrier indésirable</strong>.
                </p>
              </div>

              <Button
                onClick={handleVerify}
                disabled={loading || otp.join('').length !== 6}
                className="w-full h-12 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full"
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Verification...
                  </>
                ) : (
                  'Verifier'
                )}
              </Button>

              {/* Resend */}
              <div className="mt-6 text-center">
                {resendTimer > 0 ? (
                  <p className="text-slate-500">
                    Renvoyer le code dans <span className="font-semibold text-[#1A237E]">{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="text-[#2196F3] hover:text-[#1E88E5] font-semibold disabled:opacity-50"
                  >
                    {resending ? 'Envoi en cours...' : 'Renvoyer le code'}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>

          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-6 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </button>
        </div>
      </main>
    </div>
  );
};

export default VerifyOTP;
